import { defaultUseQueryRefetchOptions } from '@/_shared/constants.ts'
import { CreativeOSFeatures, CreativeOSPages } from '@/_shared/subscriptionPlans.ts'
import AccessControl from '@/components/AccessControl.tsx'
import { MasonryComponent, TypedResponsiveMasonry } from '@/components/ResponsiveMasonryWrapper.tsx'
import TeamsWelcomeSection from '@/components/TeamsWelcomeSection.tsx'
import TemplatesDashboard from '@/components/TemplatesDashboard.tsx'
import { OrderFilterSelect } from '@/components/ad-inspiration/OrderFilterSelect.tsx'
import { Stack, Text } from '@/components/custom-components'
import { FeatureTabs } from '@/components/custom-components/FeatureTabs/index.tsx'
import { Loader } from '@/components/custom-components/Loader'
import NotFoundBox from '@/components/custom-components/NotFound/index.tsx'
import { TemplatesFilterPopover } from '@/components/custom-components/TemplateFilterPopover/index.tsx'
import { ErrorDisplay } from '@/components/error.tsx'
import FirstTimeWelcomeDialog from '@/components/onboarding/FirstTimeWelcomeDialog.tsx'
import { AdCard } from '@/components/templates/AdCard'
import { AdGridView } from '@/components/templates/AdGridView'
import useCardLayoutWithImpressions from '@/hooks/useCardLayoutWithImpressions.tsx'
import { AdFeedFilterOption } from '@/hooks/useFilterFeed.tsx'
import { showTemplateRecentButton } from '@/utils/data/feeds.ts'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { z } from 'zod'
import { AirTableAdRecord, orderFilter as OrderFilterType } from '../../../../src/shared/airtable/index.ts'
import { trpc } from '../utils/trpc.ts'

export type SelectedTemplateFilters = {
  collectionIds: string[]
  brands?: string[]
  aspectRatios?: string[]
}

type SearchParams = {
  from?: string
  collections?: string
  getStarted?: string
  welcomePage?: boolean
  orderFilter?: z.infer<typeof OrderFilterType>
  brands?: string
  aspectRatios?: string
}

export type FilterOption = {
  title: string
  counter: number
  optionItems: { label: string; value?: boolean; logo?: string; id?: string }[]
}

export const Route = createFileRoute('/feeds/templates/')({
  component: All,
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    const from = search?.collections as string | undefined
    const collections = search?.collections as string | undefined
    const getStarted = search?.getStarted as string | undefined
    const welcomePage = search?.welcomePage as boolean
    const aspectRatios = search?.aspectRatios as string | undefined
    const brands = search?.brands as string | undefined
    const orderFilter = search?.orderFilter as z.infer<typeof OrderFilterType> | undefined

    return {
      from,
      collections,
      getStarted,
      welcomePage,
      orderFilter,
      brands,
      aspectRatios,
    }
  },
})

function All() {
  const {
    collections: queryCollections,
    getStarted,
    orderFilter,
    aspectRatios: queryAspectRatio,
    brands: queryBrands,
    welcomePage,
  } = Route.useSearch()
  const navigate = useNavigate()
  const [getStartedDialogOpen, setGetStartedDialogOpen] = useState<boolean>(false)

  useEffect(() => {
    if (getStarted === 'ready' && !getStartedDialogOpen) {
      setGetStartedDialogOpen(true) // Prevent unnecessary updates
    }
  }, [getStarted, getStartedDialogOpen])

  const [allData, setAllData] = useState<AirTableAdRecord[] | undefined>(undefined)
  const [filterOptions, setFilterOptions] = useState<AdFeedFilterOption[]>([])
  const [cursor, setCursor] = useState(0)
  const [unfilteredDataLength, setUnfilteredDataLength] = useState<number>(0)
  const [showWelcomePage, setShowWelcomePage] = useState<boolean>(welcomePage || false)

  const [selectedFilters, setSelectedFilters] = useState<SelectedTemplateFilters>({
    collectionIds: queryCollections ? queryCollections.split(',') : [],
    brands: queryBrands ? (queryBrands.split(',') as string[]) : undefined,
    aspectRatios: queryAspectRatio ? queryAspectRatio.split(',') : undefined,
  })

  const { data: brands } = trpc.getBrandsV2.useQuery(undefined, defaultUseQueryRefetchOptions)

  // Get the template collections to be passed to the filter
  const { data: templateCollections } = trpc.getAllCollections.useQuery(
    {},
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    },
  )
  useEffect(() => {
    setShowWelcomePage(welcomePage === true)
  }, [welcomePage])

  // Update the filters after fetching the templates
  useEffect(() => {
    if (templateCollections && templateCollections.length > 0 && brands && brands.length > 0) {
      setFilterOptions((prevOptions) => {
        if (prevOptions.length > 0) return prevOptions // Avoid redundant updates
        return [
          {
            title: 'Collection',
            counter: 0,
            optionItems: templateCollections.map((i) => ({
              label: i.Title,
              value: false,
              id: i.atID,
            })),
          },
          {
            title: 'Brand',
            counter: 0,
            optionItems: brands.map((i) => ({
              label: i.brandName,
              value: false,
              id: i.brandId,
              logo: i.brandImage ?? undefined,
            })),
          },
          {
            title: 'Ad Layout',
            counter: 0,
            optionItems: [
              {
                label: '9:16',
                displayName: '9:16 (Portrait / Story)',
                value: false,
              },
              { label: '16:9', displayName: '16:9 (Landscape)', value: false },
              {
                label: '4:5',
                displayName: '4:5 (Vertical Feed)',
                value: false,
              },
              {
                label: '5:4',
                displayName: '5:4 (Horizontal Feed)',
                value: false,
              },
              { label: '1:1', displayName: '1:1 (Square)', value: false },
            ],
          },
        ]
      })
    }
  }, [templateCollections, brands])

  // Update AdFilter options based on selectedFilters
  useEffect(() => {
    if (filterOptions.length === 0 || !selectedFilters) return

    const updatedOptions = filterOptions.map((option) => ({
      ...option,
      optionItems: option.optionItems.map((item) => ({
        ...item,
        value:
          (option.title === 'Collection' && selectedFilters.collectionIds?.includes(item.id ?? '')) ||
          (option.title === 'Ad Layout' && selectedFilters.aspectRatios?.includes(item.id ?? '')) ||
          (option.title === 'Brand' && selectedFilters.brands?.includes(item.id ?? '')),
      })),
    }))

    setFilterOptions((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(updatedOptions)) return prev
      return updatedOptions
    })
  }, [filterOptions, selectedFilters])

  const updateQueryString = useCallback(
    (params: {
      collections?: string
      aspectRatios?: string
      brands?: string
    }) => {
      const searchParams = new URLSearchParams()

      if (params.collections) searchParams.set('collections', params.collections)
      if (params.aspectRatios) searchParams.set('aspectRatios', params.aspectRatios)
      if (params.brands) searchParams.set('brands', params.brands)

      navigate({
        to: '/feeds/templates',
        replace: true,
        search: (old) => ({ ...old, ...params, orderFilter }),
      })
    },
    [navigate, orderFilter],
  )

  const handleOptionsChange = useCallback(
    (options: AdFeedFilterOption[]) => {
      const selectedCollections: string[] = []
      const selectedAspectRatios: string[] = []
      const selectedBrands: string[] = []

      options.forEach((group) => {
        group.optionItems.forEach((item) => {
          if (item.value) {
            if (group.title === 'Collection') selectedCollections.push(`${item.id}`)
            if (group.title === 'Ad Layout') selectedAspectRatios.push(item.label)
            if (group.title === 'Brand') selectedBrands.push(`${item.id}`)
          }
        })
      })

      setSelectedFilters({
        collectionIds: selectedCollections.length > 0 ? selectedCollections : [],
        aspectRatios: selectedAspectRatios.length > 0 ? selectedAspectRatios : undefined,
        brands: selectedBrands.length > 0 ? selectedBrands : undefined,
      })

      updateQueryString({
        collections: selectedCollections.length > 0 ? selectedCollections.join(',') : undefined,
        aspectRatios: selectedAspectRatios.length > 0 ? selectedAspectRatios.join(',') : undefined,
        brands: selectedBrands.length > 0 ? selectedBrands.join(',') : undefined,
      })
    },
    [updateQueryString],
  )

  useEffect(() => {
    if (!selectedFilters) return
    const params: Record<string, string | undefined> = {}
    setCursor(0)
    if (selectedFilters.collectionIds) params.collections = selectedFilters.collectionIds.join(',')
    if (selectedFilters.aspectRatios) params.aspectRatios = selectedFilters.aspectRatios.join(',')
    if (selectedFilters.brands) params.brands = selectedFilters.brands.join(',')
  }, [selectedFilters])

  // check for if there is at least one option selected
  const isAnyFilterSelected = (filters: SelectedTemplateFilters) => {
    return (
      (filters.collectionIds && filters.collectionIds.length > 0) ||
      (filters.aspectRatios && filters.aspectRatios.length > 0) ||
      (filters.brands && filters.brands.length > 0)
    )
  }

  const {
    data: filteredTemplatesByCollections,
    fetchNextPage,
    isLoading,
    isError,
    isFetchingNextPage,
    hasNextPage,
  } = trpc.filterTemplatesByCollections.useInfiniteQuery(
    {
      ...selectedFilters,
      sortingOptions: orderFilter,
      Tags: undefined,
      limit: 20,
      Ready: true,
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextCursor,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: Boolean(isAnyFilterSelected(selectedFilters)),
      initialCursor: cursor,
    },
  )

  const { ref: scrollRef, inView } = useInView({
    threshold: 0,
    trackVisibility: true,
    delay: 100,
    initialInView: false,
  })

  useEffect(() => {
    if (
      inView &&
      hasNextPage &&
      !isFetchingNextPage &&
      filteredTemplatesByCollections &&
      allData &&
      allData.length &&
      unfilteredDataLength < filteredTemplatesByCollections.pages[0].TotalRecords
    ) {
      fetchNextPage()
    }
  }, [inView, hasNextPage, fetchNextPage, filteredTemplatesByCollections])

  useEffect(() => {
    if (!filteredTemplatesByCollections) return

    setAllData(() => undefined)

    const records = [] as AirTableAdRecord[]
    for (const page of filteredTemplatesByCollections.pages) {
      records.push(...page.ATRecords)
    }
    setAllData(() => records.filter((item, index) => index === records.findIndex((obj) => obj.atID === item.atID)))
    setUnfilteredDataLength(records.length)
  }, [filteredTemplatesByCollections])

  const { squareRef, desiredCardWidth, columns, gutterWidth } = useCardLayoutWithImpressions()

  return (
    <AccessControl page={CreativeOSPages.AD_TEMPLATES}>
      {getStartedDialogOpen ? (
        <FirstTimeWelcomeDialog open={getStartedDialogOpen} onOpenChange={() => setGetStartedDialogOpen(false)} />
      ) : null}
      <Stack className="gap-3 lg:gap-6 relative">
        <TemplatesDashboard />
        {showWelcomePage ? (
          <TeamsWelcomeSection />
        ) : (
          <>
            <Stack id="templates" className="gap-3 lg:gap-8">
              <Stack className="gap-3 lg:gap-6">
                <div className={'flex justify-between gap-5 items-center'}>
                  <div>
                    <Text weight="semibold" size={'xxl'} className="w-fit">
                      Browse The Newest <span className="text-themedestructive">Templates</span>
                    </Text>
                  </div>
                  <div>
                    <div className="lg:hidden">
                      <OrderFilterSelect
                        defaultFilter={orderFilter || 'Recent'}
                        options={['Random', 'Recent', 'Popular']}
                      />
                    </div>
                  </div>
                </div>
              </Stack>
            </Stack>

            <div
              className={`z-10 lg:sticky lg:top-[4.4rem] lg:bg-themebackground dark:lg:bg-themebackgrounddark lg:py-2 flex justify-between items-center`}
            >
              <div className={'flex gap-4 items-center flex-wrap'}>
                <FeatureTabs
                  tabItems={[
                    {
                      name: 'Ads',
                      link: '/feeds/templates',
                      featureName: CreativeOSFeatures.AD_TEMPLATE_NAV_BUTTON,
                    },
                    {
                      name: 'Emails',
                      link: '/feeds/templates/emails',
                      featureName: CreativeOSFeatures.EMAIL_TEMPLATE_NAV_BUTTON,
                    },
                    {
                      name: 'Landers',
                      link: '/feeds/templates/landing-pages',
                      featureName: CreativeOSFeatures.LANDER_TEMPLATE_NAV_BUTTON,
                    },
                  ]}
                />
                {filterOptions && filterOptions.length > 0 && (
                  <TemplatesFilterPopover
                    initialOptions={filterOptions}
                    onOptionsChange={handleOptionsChange}
                    placeholder={'Filter Ad Templates'}
                  />
                )}
              </div>

              <div className={'hidden lg:flex items-end'}>
                <OrderFilterSelect defaultFilter={orderFilter || 'Recent'} options={['Random', 'Recent', 'Popular']} />
              </div>
            </div>
            {isLoading ? (
              <div className="flex justify-center items-center w-full h-[70vh]">
                <Loader />
              </div>
            ) : isError ? (
              <ErrorDisplay />
            ) : filteredTemplatesByCollections ? (
              <div>
                {allData && allData.length === 0 ? (
                  <NotFoundBox />
                ) : (
                  <div ref={squareRef} className={'relative w-full lg:px-0 pb-10'}>
                    {filteredTemplatesByCollections.pages[0].TotalRecords === 0 ? (
                      <div className={'lg:h-96 flex justify-center items-center'}>
                        <p>No templates added to this collection</p>
                      </div>
                    ) : (
                      allData && (
                        <div>
                          <TypedResponsiveMasonry
                            columnsCountBreakPoints={columns ? { 0: columns } : {}} // Columns is determined by the width of the container
                          >
                            <MasonryComponent gutter={gutterWidth / 16 + 'rem'}>
                              {allData.map((ad) => {
                                return (
                                  <AdCard
                                    key={ad.atID}
                                    adData={{
                                      Ad: ad,
                                      IsLocked: false,
                                      IsPublic: false,
                                      brandName: undefined,
                                      shouldInvalidateCache: false,
                                      DesiredWidth: desiredCardWidth,
                                    }}
                                    showRecentButton={showTemplateRecentButton(ad.Created)}
                                  />
                                )
                              })}
                            </MasonryComponent>
                          </TypedResponsiveMasonry>
                        </div>
                      )
                    )}
                    <div className={'relative'}>
                      <div
                        className={
                          'absolute w-[10px] h-[1500px] transform translate-y-[-1500px]' // Having the height be 1500px helps when the masonry grid has one column longer than another
                        }
                        ref={scrollRef}
                      ></div>
                    </div>{' '}
                    <div className={'relative'}>
                      <div
                        className={
                          'absolute w-[10px] h-[1500px] transform translate-y-[-1500px]' // Having the height be 1500px helps when the masonry grid has one column longer than another
                        }
                        ref={scrollRef}
                      ></div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              !isAnyFilterSelected(selectedFilters) && (
                <AdGridView
                  Filter={{
                    Expert: undefined,
                    Tags: undefined,
                    loadAdsCreatedAfter: undefined,
                    Ready: true,
                    cursor: undefined,
                    limit: 20,
                    sortingOptions: orderFilter,
                  }}
                />
              )
            )}
            <div ref={scrollRef} className="flex justify-center w-full min-h-14 h-14">
              {isFetchingNextPage ? <Loader /> : null}
            </div>
          </>
        )}
      </Stack>
    </AccessControl>
  )
}
